<template>
  <div>
    <HeaderLogin />

    <section class="mt-8">
      <v-container>
        <v-row>
          <v-col cols="12" sm="8" offset-sm="2" md="6" offset-md="3">
            <v-card
              color="accent lighten-3"
              elevation="0"
              :class="!localLogin ? 'py-8' : 'py-4'"
            >
              <v-card-text class="py-8">
                <v-slide-y-transition>
                  <v-alert
                    v-show="showCustomMessage"
                    type="info"
                    color="secondary"
                    text
                  >
                    <span v-html="$t('ng.custom_login_message')" />
                  </v-alert>
                </v-slide-y-transition>
                <v-form v-model="valid">
                  <v-row v-if="localLogin">
                    <v-col cols="12">
                      <v-text-field
                        v-model="email"
                        outlined
                        dense
                        hide-details
                        background-color="white"
                        class="red--text"
                        :rules="[rules.email, rules.notEmpty]"
                        :label="$t('views.profile.email')"
                      />
                    </v-col>
                    <v-col v-if="!showPasswordReset" cols="12">
                      <v-text-field
                        v-model="password"
                        outlined
                        dense
                        hide-details
                        :rules="[rules.notEmpty]"
                        background-color="white"
                        :type="showPassword ? 'text' : 'password'"
                        :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                        :label="$t('views.profile.yourPassword')"
                        @click:append="showPassword = !showPassword"
                      />
                    </v-col>
                  </v-row>
                  <v-row v-if="!showPasswordReset && localLogin">
                    <v-expand-transition>
                      <v-col v-if="showMaxRequestWarning" cols="12">
                        <v-alert
                          v-model="showMaxRequestWarning"
                          dismissible
                          dense
                          type="error"
                          ><span class="caption">{{
                            $t("ng.show_max_request_warning", {
                              time: requestTimer,
                            })
                          }}</span></v-alert
                        >
                      </v-col>
                    </v-expand-transition>
                    <v-col cols="12">
                      <v-btn
                        color="primary"
                        block
                        depressed
                        :disabled="!valid || showMaxRequestWarning"
                        :loading="loading"
                        @click="localeLogin()"
                      >
                        {{ $t("views.login.login") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                  <v-row v-if="!localLogin">
                    <v-col cols="12">
                      <p
                        class="text-center"
                        v-html="
                          $t('ng.login_sso_info', {
                            client: settings.instance_name,
                          })
                        "
                      ></p>
                    </v-col>
                  </v-row>
                  <v-row v-if="!showPasswordReset">
                    <v-col
                      v-for="(client, i) in authClients"
                      :key="i"
                      cols="12"
                      class="pt-0"
                    >
                      <v-btn
                        color="secondary"
                        block
                        depressed
                        :disabled="showMaxRequestWarning"
                        :href="client.url"
                      >
                        {{ $t("views.login.login") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                  <v-row v-if="showPasswordReset && localLogin">
                    <v-expand-transition>
                      <v-col v-if="showMaxRequestWarning" cols="12">
                        <v-alert
                          v-model="showMaxRequestWarning"
                          dismissible
                          dense
                          type="error"
                          ><span class="caption">{{
                            $t("ng.show_max_request_warning", {
                              time: requestTimer,
                            })
                          }}</span></v-alert
                        >
                      </v-col>
                    </v-expand-transition>
                    <v-col cols="12">
                      <v-btn
                        color="primary"
                        block
                        depressed
                        :disabled="!valid || loading || showMaxRequestWarning"
                        :loading="loading"
                        @click="resetPassword()"
                      >
                        {{ $t("ng.password_reset") }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-form>

                <v-row
                  v-if="localLogin"
                  :dense="$vuetify.breakpoint.xsOnly"
                  class="mt-2"
                >
                  <v-col
                    v-if="!showPasswordReset"
                    cols="12"
                    sm="6"
                    class="text-center"
                  >
                    <v-btn
                      text
                      x-small
                      color="text"
                      @click="showPasswordReset = true"
                    >
                      {{ $t("views.login.forgotPassword") }}
                    </v-btn>
                  </v-col>

                  <v-col
                    v-if="!showPasswordReset"
                    cols="12"
                    sm="6"
                    class="text-center"
                  >
                    <v-btn text x-small color="text" :to="{ name: 'Register' }">
                      {{ $t("views.login.register") }}
                    </v-btn>
                  </v-col>

                  <v-col v-if="showPasswordReset" cols="12" class="text-center">
                    <v-btn
                      text
                      x-small
                      color="secondary"
                      @click="showPasswordReset = false"
                    >
                      {{ $t("ng.login_goTo") }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <section class="mt-2">
      <v-container>
        <v-row dense>
          <v-col
            v-if="$i18n.availableLocales.length > 1"
            cols="12"
            sm="8"
            offset-sm="2"
            md="6"
            offset-md="3"
          >
            <LanguageSelector />
          </v-col>
          <v-col cols="12" class="text-center">
            <SupportDialog color="text" />
            <v-btn
              v-if="privacyUrl"
              text
              small
              :href="privacyUrl"
              target="_blank"
            >
              {{ $t("ng.privacy") }}
            </v-btn>
            <v-btn
              v-if="imprintUrl"
              text
              small
              :href="imprintUrl"
              target="_blank"
            >
              {{ $t("views.login.imprint") }}
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </section>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import LanguageSelector from "@/components/_app/LanguageSelector.vue";
import HeaderLogin from "@/components/_layout/HeaderLogin";
import SupportDialog from "@/components/_app/dialogs/SupportDialog";

export default {
  name: "LoginView",
  components: {
    LanguageSelector,
    HeaderLogin,
    SupportDialog,
  },
  data() {
    return {
      email: "",
      password: "",
      loading: false,
      showPassword: false,
      showPasswordReset: false,
      valid: true,
      rules: {
        email: (value) =>
          (!!value && value.match(/\S+@\S+\.\S+/) != null) ||
          this.$t("views.login.invalidEmail", this.$i18n.locale),
        notEmpty: (value) =>
          !!value || this.$t("views.login.required", this.$i18n.locale),
      },
      showMaxRequestWarning: false,
      requestTimer: 10,
    };
  },
  computed: {
    ...mapGetters("app", { language: "get_language" }),
    ...mapGetters("auth", { auth_sources: "get_auth_sources" }),
    ...mapGetters("community", { settings: "get_settings" }),
    title() {
      return process.env.VUE_APP_TITLE;
    },
    imprintUrl() {
      return process.env.VUE_APP_IMPRINT_URL;
    },
    privacyUrl() {
      if (!process.env.VUE_APP_CONSENT_PATH) return false;
      return `${process.env.VUE_APP_CONSENT_PATH}/consent-${this.$i18n.locale}.html`;
    },
    authClients() {
      if (!this.auth_sources) return [];
      let sources = this.auth_sources.filter((s) => s.name !== "local");
      let clients = [];
      sources.forEach((s) => {
        s.clients.forEach((c) => {
          if (c.is_active) clients.push(c);
        });
      });
      return clients;
    },
    localLogin() {
      if (!this.auth_sources) return true;
      return !!this.auth_sources.filter((s) => s.name === "local")[0];
    },
    redirectLink() {
      if (!this.$route.query || !this.$route.query.next) return null;
      if (this.$route.query.next && Object.keys(this.$route.query).length === 1)
        return this.$route.query.next;
      var host = window.location.host;
      var path = `${this.$route.path}?next=`;
      var fullPath = this.$route.fullPath;
      var result = decodeURIComponent(fullPath.replace(path, ""));
      return `https://${host}${result}`;
    },
    showCustomMessage() {
      return process.env.VUE_APP_CUSTOM_LOGIN_MSG === "true";
    },
  },
  beforeMount() {
    this.initNavigation();
  },
  methods: {
    ...mapActions("auth", ["reset_password", "get_token"]),
    ...mapActions("community", ["fetch_settings"]),
    ...mapMutations("app", [
      "set_topnavigation_settings",
      "set_bottomnavigation_settings",
      "set_loading",
    ]),
    initNavigation() {
      this.set_topnavigation_settings({
        visible: false,
      });
      this.set_bottomnavigation_settings({
        visible: false,
      });
    },
    async localeLogin() {
      this.loading = true;
      this.set_loading(true);
      let payload = {
        email: this.email,
        password: this.password,
        language: this.$i18n.locale,
        auth_source: "local",
        external_token: "",
      };

      let res = await this.get_token(payload);
      if (res.success) {
        localStorage.setItem("locale", this.$i18n.locale);
        this.loading = false;
        this.set_loading(false);
        if (this.redirectLink) {
          if (this.redirectLink.includes("oauth")) {
            window.location.href = this.redirectLink;
          } else {
            this.$router.push(this.redirectLink);
          }
        } else {
          this.$router.push("/start");
        }
      } else {
        this.loading = false;
        this.set_loading(false);
        if (res._status === 429) {
          this.showMaxRequestWarning = true;
          this.requestTimer = 10;
          this.initTimer();
          return false;
        }
        this.$notify({
          type: "error",
          title: this.$t("views.subscriptions.error"),
          text: this.$t("toasters.userDoesNotExist"),
        });
      }

      this.loading = false;
    },
    async resetPassword() {
      this.loading = true;
      this.set_loading(true);
      let payload = {
        email: this.email,
        language: this.$i18n.locale,
      };

      let res = await this.reset_password(payload);
      this.loading = false;
      this.set_loading(false);

      if (res && res._status === 200) {
        this.email = "";
        (this.showPasswordReset = false),
          this.$notify({
            type: "success",
            title: this.$t("views.subscriptions.success"),
            text: this.$t("toasters.passwordReset"),
          });
      } else {
        if (res._status === 429) {
          this.showMaxRequestWarning = true;
          this.requestTimer = 60;
          this.initTimer();
          return false;
        }
        this.$notify({
          type: "error",
          title: this.$t("views.subscriptions.error"),
          text: this.$t("toasters.error"),
        });
      }
    },
    initTimer() {
      if (this.requestTimer > 0) {
        setTimeout(() => {
          this.requestTimer -= 1;
          this.initTimer();
        }, 1000);
      } else {
        this.showMaxRequestWarning = false;
        this.requestTimer = 10;
      }
    },
  },
};
</script>
